import { BigNumberOccurrence } from "../../../models/occurrence";
import { useLocation } from "react-router-dom";
import { formatMilisecondsInDaysHours } from "../../../utils/dateUtils";
import { useEffect, useLayoutEffect, useRef } from "react";

const useFitFont = (
  wrapperRef: React.RefObject<HTMLDivElement>,
  contentRef: React.RefObject<HTMLDivElement>,
  deps: React.DependencyList,
  max = 20,
  min = 10
) => {
  const fitRef = useRef<() => void>(() => {});

  fitRef.current = () => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;
    const style = getComputedStyle(wrapper);
    const hPad = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0');
    const availableWidth = wrapper.clientWidth - hPad;
    let size = max;
    content.style.fontSize = `${size}px`;
    while (size > min && content.scrollWidth > availableWidth) {
      size -= 0.5;
      content.style.fontSize = `${size}px`;
    }
  };

  useLayoutEffect(() => fitRef.current(), deps);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => fitRef.current());
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
};

export const BigNumbers = (props: {
  bigNumberAnalytics: BigNumberOccurrence;
  finishedFilter: string[];
  loading?: boolean;
  isFilteredByStep?: boolean | null;
}) => {
  const location = useLocation();
  const isHistorico = location.pathname.includes("history");

  const colorPrimary = isHistorico ? "text-color-dark" : "text-color-primary";
  const colorWarning = isHistorico ? "text-color-dark" : "text-color-warning";

  const quantityWidgets = [
    (props.finishedFilter.includes('true') || props.finishedFilter.length === 0) && {
      label: "Chamados concluídos",
      value: Math.max(props.bigNumberAnalytics.totalOfFinalizedOccurrence, 0),
      color: colorPrimary,
    },
    (!props.isFilteredByStep && props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados não iniciados",
      value: Math.max(props.bigNumberAnalytics.totalOfNotStartedOccurrence, 0),
      color: colorPrimary,
    },
    (props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados em andamento",
      value: Math.max(props.bigNumberAnalytics.totalOfInProgressOccurrence, 0),
      color: colorPrimary,
    },
    {
      label: "Veículos",
      value: Math.max(props.bigNumberAnalytics.totalChassis ?? 0, 0),
      color: colorPrimary,
    },
  ].filter((el): el is { label: string; value: number; color: string } => el !== false);

  const timeWidgets = [
    { label: "Tempo total em serviço", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.totalInService * 1000) || '0h', color: colorWarning },
    { label: "Tempo médio por veículo", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInService * 1000) || '0h', color: colorWarning },
    { label: "Tempo médio por chamado", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInServicePerOccurrence * 1000) || '0h', color: colorWarning },
  ];

  const wrapper1Ref = useRef<HTMLDivElement>(null);
  const content1Ref = useRef<HTMLDivElement>(null);
  const wrapper2Ref = useRef<HTMLDivElement>(null);
  const content2Ref = useRef<HTMLDivElement>(null);

  useFitFont(wrapper1Ref, content1Ref, [quantityWidgets.length, props.loading, props.bigNumberAnalytics], 22, 7);
  useFitFont(wrapper2Ref, content2Ref, [props.loading, props.bigNumberAnalytics], 22, 7);

  const renderItems = (items: { label: string; value: any; color: string }[]) =>
    items.map((item, index) => (
      <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0.6em' }}>
          <span className={item.color} style={{ fontSize: '2.4em', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {props.loading ? '-' : item.value}
          </span>
          <span style={{ fontSize: '1em', color: '#555', marginTop: 2, whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
        </div>
        {index !== items.length - 1 && (
          <div style={{ width: 1, height: '2.5em', background: '#ccc', flexShrink: 0 }} />
        )}
      </div>
    ));

  const cardWrapperStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '8px 16px',
    overflow: 'hidden',
    flex: '1 1 0%',
    minWidth: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%', alignItems: 'stretch' }}>

      <div ref={wrapper1Ref} style={cardWrapperStyle}>
        <div ref={content1Ref} style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center' }}>
          {renderItems(quantityWidgets)}
        </div>
      </div>

      <div ref={wrapper2Ref} style={cardWrapperStyle}>
        <div ref={content2Ref} style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center' }}>
          {renderItems(timeWidgets)}
        </div>
      </div>

    </div>
  );
};

